import { useState } from 'react';
import { skillsApi } from '../services/api';
import { useAlert } from '../contexts/AlertContext';
import { COMMENTS_REQUIRED, MIN_COMMENTS_LENGTH } from '../constants/assignmentConstants';
import { REVIEW_TYPES } from '../constants/reviewTypes';

const useAssignmentStateMachine = (
  assignment, 
  myReviewer, 
  competencies, 
  defaultLevelId, 
  getOverallLevel, 
  getSegmentLevel,
  triggerRefresh
) => {
  const { showAlert } = useAlert();
  const [activeStep, setActiveStep] = useState(0);
  const [startingAssignment, setStartingAssignment] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Step validation logic
  const isStepComplete = (stepIndex) => {
    // Start Assignment step is always considered complete
    if (stepIndex === 0) {
      return true;
    }
    
    // For summary step, check if reviewer has completed the assessment
    if (stepIndex === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 1) {
      return Boolean(myReviewer?.completedAt);
    }
    
    // For actions step, check if feedback has been attached
    if (stepIndex === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 2) {
      // If already submitted, consider complete
      if (assignment?.reviewsCompletedAt || myReviewer?.feedbackAttachedAt) {
        return true;
      }
      
      // Check if all three feedback actions meet minimum length requirement
      const startComments = myReviewer?.feedbackOnDemand?.startComments || '';
      const stopComments = myReviewer?.feedbackOnDemand?.stopComments || '';
      const continueComments = myReviewer?.feedbackOnDemand?.continueComments || '';
      
      if (COMMENTS_REQUIRED) {
        const allCommentsValid = 
          startComments.length >= MIN_COMMENTS_LENGTH &&
          stopComments.length >= MIN_COMMENTS_LENGTH &&
          continueComments.length >= MIN_COMMENTS_LENGTH;
        
        return allCommentsValid;
      }
      
      return true;
    }
    
    // Adjust index for actual skills (subtract 1 because first step is Start Assignment)
    const currentTemplate = myReviewer?.skillsMatrixTemplate?.skillsTemplates?.[stepIndex - 1];
    if (!currentTemplate) {
      return false;
    }

    // If skill is not required, step is always considered complete
    if (currentTemplate.isRequired === false) {
      return true;
    }

    // Check overall competency level. A level that was never picked is
    // `undefined`/`null` here (the comments autosave creates the response row
    // with a null level), and the server rejects submit for a required skill
    // without one — so treat "unset" exactly like "left at the default" (ADR-195).
    const overallLevel = getOverallLevel(stepIndex - 1);
    const hasMappings = currentTemplate.mappings?.length > 0;
    if (!hasMappings && (overallLevel == null || overallLevel === defaultLevelId)) {
      return false;
    }

    const currentTemplateHasMappings = currentTemplate.mappings?.length > 0;
    // If there are mappings, check that all are provided
    if (currentTemplateHasMappings) {
      const allSkillsProvided = currentTemplate.mappings.every(mapping => {
        const segmentLevel = getSegmentLevel(stepIndex - 1, mapping.segment.id);
        return segmentLevel !== defaultLevelId;
      });
      if (!allSkillsProvided) {
        return false;
      }

      // Also check that all segment mappings have sufficient comments
      const reviewerId = myReviewer.id;
      const skillTemplateId = currentTemplate.id;
      
      const allSegmentCommentsValid = currentTemplate.mappings.every(mapping => {
        // Segment comments are stored under: reviewers[reviewerId].skills[skillTemplateId].segments[segmentId].comments
        const segmentComments = competencies.reviewers?.[reviewerId]?.skills?.[skillTemplateId]?.segments?.[mapping.segment.id]?.comments || '';
        const isValid = !COMMENTS_REQUIRED || segmentComments.length >= MIN_COMMENTS_LENGTH;

        return isValid;
      });
      if (!allSegmentCommentsValid) {
        return false;
      }
    }

    // If there are mappings, return true as we don't need to check the comments
    // they are not visible
    if (currentTemplateHasMappings) {
      return true;
    }

    // If there are no mappings, check if the overall competency has sufficient comments
    // Check required comments validation
    const reviewerId = myReviewer.id;
    const skillTemplateId = currentTemplate.id;
    const comments = competencies.reviewers?.[reviewerId]?.skills?.[skillTemplateId]?.comments || '';
    
    // Use component-level constants for comments validation
    if (COMMENTS_REQUIRED && comments.length < MIN_COMMENTS_LENGTH) {
      return false;
    }

    return true;
  };

  const isAllPreviousStepsComplete = () => {
    if (!myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length) return false;
    return myReviewer.skillsMatrixTemplate.skillsTemplates.every((_, index) => isStepComplete(index + 1));
  };

  // Validation function specifically for feedback actions
  const areFeedbackActionsValid = () => {
    // If already submitted, they're valid
    if (assignment?.reviewsCompletedAt || myReviewer?.feedbackAttachedAt) {
      return true;
    }
    
    if (!COMMENTS_REQUIRED) {
      return true;
    }
    
    const startComments = myReviewer?.feedbackOnDemand?.startComments || '';
    const stopComments = myReviewer?.feedbackOnDemand?.stopComments || '';
    const continueComments = myReviewer?.feedbackOnDemand?.continueComments || '';
    
    return startComments.length >= MIN_COMMENTS_LENGTH &&
           stopComments.length >= MIN_COMMENTS_LENGTH &&
           continueComments.length >= MIN_COMMENTS_LENGTH;
  };

  // Step navigation
  const handleStep = (step, isDirty) => () => {
    if (isDirty) return; // Don't allow step changes while saving
    
    if (step === activeStep) return; // Allow clicking current step
    if (step === 0) return setActiveStep(0); // Always allow clicking Start Assignment
    
    if (!myReviewer?.startedAt) {
      showAlert('Please start the assignment first.', 'warning');
      return;
    }

    if (step < activeStep) return setActiveStep(step); // Allow going back
    
    // Check if all previous steps are complete
    for (let i = 0; i < step; i++) {
      if (!isStepComplete(i)) {
        showAlert('Please complete all previous steps before proceeding.', 'warning');
        return;
      }
    }
    
    // Only allow going forward if current step is complete
    if (isStepComplete(activeStep)) {
      setActiveStep(step);
    } else {
      // Customize message based on the current step
      if (activeStep === myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length + 2) {
        showAlert(`Please complete all feedback actions before proceeding:
• Add START actions (minimum ${MIN_COMMENTS_LENGTH} characters)
• Add STOP actions (minimum ${MIN_COMMENTS_LENGTH} characters)
• Add CONTINUE actions (minimum ${MIN_COMMENTS_LENGTH} characters)`, 'warning');
      } else {
        showAlert(`Please complete all requirements in the current step before proceeding:
• Select overall competency level
• Assess all skill segments
• Add comments (minimum ${MIN_COMMENTS_LENGTH} characters) for overall competency and all skill segments`, 'warning');
      }
    }
  };

  // Assignment actions
  const handleStartAssignment = async (isDirty) => {
    if (isDirty) return; // Don't allow starting assignment while saving
    
    try {
      setStartingAssignment(true);
      await skillsApi.startEmployeeAssignment(myReviewer.id);
      setActiveStep(1);
      triggerRefresh(); // Trigger data refresh
    } catch (error) {
      console.error('Error starting assignment:', error);
      showAlert(error.message || 'Failed to start assignment', 'error', error);
    } finally {
      setStartingAssignment(false);
    }
  };

  const handleSubmitAssignment = async (isDirty) => {
    if (isDirty) return; // Don't allow submissions while saving
    
    try {
      // Set submitting state to true to show loading indicator
      setSubmitting(true);
      
      await skillsApi.submitEmployeeAssignment(myReviewer.id);
      showAlert('Assignment submitted successfully!', 'success');
      return true; // Indicate successful submission for navigation
    } catch (error) {
      console.error('Error submitting assignment:', error);
      showAlert(error.message || 'Failed to submit assignment', 'error', error);
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-set initial step based on assignment status - resume from first incomplete step
  const initializeStep = () => {
    if (myReviewer?.startedAt && activeStep === 0) {
      const totalSkillSteps = myReviewer?.skillsMatrixTemplate?.skillsTemplates?.length || 0;

      // Find first incomplete step (starting from step 1)
      for (let step = 1; step <= totalSkillSteps; step++) {
        if (!isStepComplete(step)) {
          setActiveStep(step);
          return;
        }
      }

      // All skill steps complete - check summary step
      const summaryStepIndex = totalSkillSteps + 1;
      if (!isStepComplete(summaryStepIndex)) {
        setActiveStep(summaryStepIndex);
        return;
      }

      // Summary complete - check if actions step should be shown and is incomplete
      const actionsStepIndex = totalSkillSteps + 2;
      const shouldShowActions = myReviewer?.reviewType === REVIEW_TYPES.SEQUENTIAL &&
                                myReviewer?.order !== 1 &&
                                myReviewer?.completedAt;

      if (shouldShowActions && !isStepComplete(actionsStepIndex)) {
        setActiveStep(actionsStepIndex);
        return;
      }

      // Default to summary if everything is complete
      setActiveStep(summaryStepIndex);
    }
  };

  return {
    // State
    activeStep,
    setActiveStep,
    startingAssignment,
    submitting,
    
    // Validation functions
    isStepComplete,
    isAllPreviousStepsComplete,
    areFeedbackActionsValid,
    
    // Actions
    handleStep,
    handleStartAssignment,
    handleSubmitAssignment,
    initializeStep
  };
};

export default useAssignmentStateMachine; 